import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { OverFullWindow } from 'components/UI';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import {
    ClientTaggedItem,
    PetTaggedItem,
    PetTagged,
    ClientTagged,
    MarketplaceClientTag,
    MarketplacePetTag
} from '@spike/marketplace-model';
import { fetchTaggedThunk, exportTaggedCsvThunk, TaggedContext, TaggedEntityType } from '@spike/marketplace-action';
import { useSettingsApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import styles from './TagUsage.module.scss';
import { formatPhoneNumber } from '@spike/phone-utils';
import Spinner from 'components/UI/V3/Spinner';
import { useMediaQuery } from 'react-responsive';
import { isTemporalEmail } from 'utils/ValidationUtils';

const ENTITY_TYPE_MAP: Record<'pet' | 'client', TaggedEntityType> = {
    pet: 'pets',
    client: 'customers'
};

const DEFAULT_PAGE_SIZE = 10;

interface TagUsageProps {
    open: boolean;
    tag: MarketplaceClientTag | MarketplacePetTag | null;
    entityType: 'pet' | 'client';
    context: TaggedContext;
    onClose: () => void;
}

export const TagUsage: React.FC<TagUsageProps> = ({ open, tag, entityType, context, onClose }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useSettingsApiClientWrapper();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [exporting, setExporting] = useState(false);
    const isMobile = useMediaQuery({ query: '(max-width:425px)' });
    const mappedEntityType = ENTITY_TYPE_MAP[entityType];
    const loading = useSelector<RootState, boolean>(state => state.marketplace.loading);
    const isTableLoading = loading && !exporting;
    const tagged = useSelector<RootState, PetTagged | ClientTagged>(state =>
        entityType === 'client' ? state.marketplace.clientTagged : state.marketplace.petTagged
    );

    useEffect(() => {
        if (!open) {
            setPage(1);
            return;
        }
        dispatch(
            fetchTaggedThunk(apiClientWrapper, {
                entityType: mappedEntityType,
                context,
                tag: tag!.id,
                page,
                size: pageSize
            })
        );
    }, [open, page, pageSize, tag!.id, context]);

    const handlePageSizeChange = useCallback((newSize: number) => {
        setPageSize(newSize);
        setPage(1);
    }, []);

    const handleExportCsv = useCallback(() => {
        setExporting(true);
        (
            dispatch(
                exportTaggedCsvThunk(apiClientWrapper, {
                    entityType: mappedEntityType,
                    context,
                    tag: tag!.id,
                    page,
                    size: pageSize
                })
            ) as unknown as Promise<void>
        ).finally(() => setExporting(false));
    }, [apiClientWrapper, mappedEntityType, context, tag!.id, dispatch]);

    if (!open) return null;

    const description =
        entityType === 'client'
            ? 'Below is a list of all clients associated with the tag you selected.'
            : 'Below is a list of all pets associated with the tag you selected.';

    const csvHeaders =
        entityType === 'client'
            ? ['Client Name', 'Email', 'Phone Number', 'Pets']
            : ['Pet Name', 'Breed', 'Pet Type', 'Client Name'];
    return (
        <OverFullWindow
            onClose={onClose}
            showMobileLogo={true}
        >
            <div className={styles.contentWrapper}>
                {isMobile && (
                    <div className={styles.mobileExport}>
                        <ExportCSV
                            headers={csvHeaders}
                            records={[]}
                            filename={`${tag!.id}-tag-usage.csv`}
                            exporting={exporting}
                            onClick={handleExportCsv}
                        />
                    </div>
                )}
                <div className={styles.header}>
                    <div className={styles.headerText}>
                        <h2 className={styles.title}>{tag!.name}</h2>
                        <p className={styles.description}>{description}</p>
                    </div>
                    {!isMobile && (
                        <ExportCSV
                            headers={csvHeaders}
                            records={[]}
                            filename={`${tag!.id}-tag-usage.csv`}
                            exporting={exporting}
                            onClick={handleExportCsv}
                        />
                    )}
                </div>

                <div className={styles.tableRoot}>
                    <Table>
                        <TableHead className={styles.tableHead}>
                            <TableRow>
                                {entityType === 'client' ? (
                                    <>
                                        <TableCell>Client Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone Number</TableCell>
                                        <TableCell>Pets</TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell>Pet Name</TableCell>
                                        <TableCell>Breed</TableCell>
                                        <TableCell>Pet Type</TableCell>
                                        <TableCell>Client Name</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        {!isTableLoading && (
                            <TableBody className={styles.tableBody}>
                                {entityType === 'client'
                                    ? (tagged.data as ClientTaggedItem[]).map((record, i) => (
                                          <TableRow key={i}>
                                              <TableCell>{record.clientName}</TableCell>
                                              <TableCell>
                                                  {isTemporalEmail(record.email) || !record.email ? '' : record.email}
                                              </TableCell>
                                              <TableCell>
                                                  {formatPhoneNumber(record.phone, apiClientWrapper.timeZone)}
                                              </TableCell>
                                              <TableCell>{record.pets}</TableCell>
                                          </TableRow>
                                      ))
                                    : (tagged.data as PetTaggedItem[]).map((record, i) => (
                                          <TableRow key={i}>
                                              <TableCell>{record.petName}</TableCell>
                                              <TableCell>
                                                  {!record.breed.includes('Not defined') ? record.breed : '—'}
                                              </TableCell>
                                              <TableCell>{record.petType}</TableCell>
                                              <TableCell>{record.clientName}</TableCell>
                                          </TableRow>
                                      ))}
                            </TableBody>
                        )}
                    </Table>
                    {isTableLoading && (
                        <div className={styles.spinnerCell}>
                            <Spinner size={24} />
                        </div>
                    )}
                    {!isTableLoading && tagged.data.length === 0 && (
                        <p className={styles.emptyMessage}>No records found for this tag.</p>
                    )}
                </div>

                <div className={styles.paginationContainer}>
                    <Pagination
                        page={page}
                        totalPages={tagged.meta.totalPages}
                        size={pageSize}
                        onPageChange={setPage}
                        onSizeChange={handlePageSizeChange}
                    />
                </div>
            </div>
        </OverFullWindow>
    );
};

export default TagUsage;
